import { ErrorMessagesInit } from '~/utils/errors/errorMessages';
import SRError from '~/utils/errors/srError';
import { DynamicComponentPaths, DynamicComponents } from './dynamicComponents';
import { DynamicFallbackComponent } from './dynamicFallbackComponent';

const loadDynamicComponent = async <Component extends keyof DynamicComponents>(
    componentName: Component,
    { pluginName, debug }: { pluginName: string | undefined; debug: boolean }
): Promise<DynamicComponents[Component]> => {
    const path = DynamicComponentPaths[componentName];

    if (!pluginName) {
        try {
            const module = await import(/* webpackExclude: /\.d.ts$/ */ `../../components/${path}`);
            return module.default;
        } catch (err) {
            if (debug) {
                console.log(err);
                console.error('Cannot find SERP dynamic component' + `/components/${path}`);
                throw new SRError(ErrorMessagesInit.NO_DYNAMIC_COMPONENT, err);
            }
        }
    }

    try {
        const module = await import(
            /* webpackExclude: /\.d.ts$/ */ `../../plugins/packages/${pluginName}/components/${path}`
        );
        return module.default;
    } catch (error1) {
        try {
            const module = await import(/* webpackExclude: /\.d.ts$/ */ `../../components/${path}`);
            return module.default;
        } catch (err) {
            if (debug) {
                console.error('Cannot find SERP dynamic component' + `/components/${path}`);
                throw new SRError(ErrorMessagesInit.NO_DYNAMIC_COMPONENT, err);
            }

            return DynamicFallbackComponent;
        }
    }
};

/**
 * Loads all dynamic components.
 *
 * @remarks
 * Dynamic components are loaded from plugins or the main application and override the default component.
 * All dynamic components are specified in {@link DynamicComponentPaths}.
 * {@link useDynamicComponents} hook for rendering dynamic components.
 *
 * @param pluginName - The name of the plugin to load the dynamic components from.
 *
 * @returns promise that resolves to an object with all dynamic components
 */
export const loadDynamicComponents = async ({
    pluginName,
    debug,
}: {
    pluginName: string | undefined;
    debug: boolean;
}): Promise<DynamicComponents> => {
    const componentKeys = Object.keys(DynamicComponentPaths);
    const componentPromises = componentKeys.map(async (componentName) => {
        return loadDynamicComponent(componentName as keyof DynamicComponents, { pluginName, debug });
    });
    const components = await Promise.all(componentPromises);

    return components.reduce((acc, component, index) => {
        acc = { ...acc, [componentKeys[index]]: component };
        return acc;
    }, {} as DynamicComponents);
};
